/**
 * NodeModal.tsx — the node editor.
 *
 * Header (#5): clickable icon (opens IconPickerModal) + a kind <select>.
 * Body (#6): Name (no label, placeholder) with a trailing ID button in its
 *   addon (click → toggles a small ID edit input below with an "ID" label
 *   addon + a check button to save). Immediately under name/ID comes the
 *   live "tick" readout (Inflow/Outflow/Stock + computed-metric values,
 *   labelled by metric name), then per-node metrics, then parameters.
 * Node Metrics: a flat list of metric names; "+ Add" opens the full
 *   MetricEditModal (name + id pattern + expression textarea per row).
 * Parameters (#7): one row = key input (placeholder, no label) + slider + a
 *   value readout + range + a trash-icon input-extension button. No
 *   duplicated number input, no "key:" label.
 * Delete (#8): no delete button here — deletion is via the canvas context
 *   menu (gated by ConfirmDialog).
 *
 * Read-only: shows the same shape, disabled.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash, Check, Plugs, Lightning as Bolt, Link, LinkBreak } from '@phosphor-icons/react';

import type { ComputedMetric, GateFn, GraphNode, NodeDisplayValue, NodeGroup, NodeKind, NodeValues, ParamSpec } from '../engine/types';
import { lookupIcon } from '../graph/icons';
import { groupColorOf } from '../graph/groups';
import { Modal } from './Modal';
import { ExprField } from './ExprField';
import { IconPickerModal } from './IconPickerModal';
import { MetricsEditModal } from './MetricsEditModal';
import { AgentModal } from './AgentModal';
import { TriggerSettingsModal } from './TriggerSettingsModal';
import { usePrompt } from './PromptDialog';
import { BindingBadge, CellBindingPicker, type BindingTools } from './CellBindingPicker';
import { lookupBoundCell } from '../engine/bindings';
import type { LibraryAdapter } from '../library/LibraryAdapter';
import type { RemoteProjectAdapter } from '../projects/RemoteProjectAdapter';


interface NodeModalProps {
  node: GraphNode;
  values?: NodeValues;
  /** The formula scope for live-eval (self.*, nodes.*, edges.*). Optional —
   *  built externally by the App from the latest tick. */
  scope?: Record<string, unknown>;
  editable: boolean;
  /** All existing node ids — for ID-uniqueness validation on rename. */
  existingIds?: string[];
  onClose: () => void;
  onSetParam: (key: string, value: number) => void;
  onUpdate?: (node: GraphNode) => void;
  onSetParamKey?: (oldKey: string, newKey: string) => void;
  /** Rename the node id (rewrites edge source/target + metric nodeId refs).
   *  Returns true if accepted (unique). */
  onRenameNode?: (oldId: string, newId: string) => boolean;
  bundle?: import('../engine/types').ScenarioBundle;
  /** Library adapter for the agent modal's file/linked-document picker. */
  libraryAdapter?: LibraryAdapter;
  /** Project backend for the agent modal's Code-tab source-file picker. */
  projectAdapter?: RemoteProjectAdapter;
  /** The project id whose `src/` to list in the Code-tab picker. */
  projectId?: string;
  /** Replace the bundle's whole workflow-trigger list (the trigger
   *  contract) — the Triggers section edits this node's entries and writes
   *  the full list through it. */
  onSetWorkflowTriggers?: (triggers: import('../engine/types').WorkflowTrigger[]) => void;
  /** Fire a workflow run NOW (worker-ensure + run start). Present = the
   *  Triggers section shows a ▶ button per row; rejects with a readable
   *  reason the modal renders inline. */
  onFireWorkflow?: (workflow: string, input: unknown[]) => Promise<void>;
  /** Live ⚡ trigger status (last fired step / last error per trigger id) —
   *  rendered under each of this node's trigger rows. */
  triggerStatus?: import('./TriggersPanel').TriggerStatusMap;
  /** Captured tick history — feeds the TriggerSettingsModal's dry-run (the
   *  expr evaluated over real ticks with the backend's firing semantics). */
  recentTicks?: import('../engine/types').TickPayload[];
  /** True when the ticks displayed come from an external model subprocess
   *  (api/relay mode) rather than the in-browser engine — the value
   *  readouts + routing section say so, because in that mode editing
   *  weights/params only affects local edit mode, not the running model. */
  flowsFromModel?: boolean;
  /** Update an out-edge (the routing table's "Adopt live split" action
   *  writes the live shares back as authored weights). */
  onUpdateEdge?: (edge: import('../engine/types').GraphEdge) => void;
  /** DB access for param cell bindings (ParamSpec.binding): the tables
   *  snapshot, its lazy load, and the PATCH seam for slider write-through.
   *  Absent in local mode — binding controls then render as broken. */
  bindingTools?: BindingTools;
}

const NODE_KINDS: NodeKind[] = ['source', 'sink', 'buffer', 'actor', 'gate', 'delay', 'assembler'];
const GATE_FNS: GateFn[] = ['pass', 'threshold', 'argmax', 'balance', 'formula'];

export function NodeModal({
  node,
  values,
  scope,
  editable,
  existingIds = [],
  onClose,
  onSetParam,
  onUpdate,
  onSetParamKey,
  onRenameNode,
  bundle,
  libraryAdapter,
  projectAdapter,
  projectId,
  onSetWorkflowTriggers,
  onFireWorkflow,
  triggerStatus,
  recentTicks,
  flowsFromModel,
  onUpdateEdge,
  bindingTools,
}: NodeModalProps): JSX.Element {
  const [draft, setDraft] = useState<GraphNode>(() => structuredClone(node));
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [editingId, setEditingId] = useState(false);
  const [idDraft, setIdDraft] = useState(node.id);
  const [metricModalOpen, setMetricModalOpen] = useState(false);
  // ⚡ Fire-now bookkeeping (the Triggers section): which trigger id has a
  // fire in flight, and the last fire error to render inline.
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  // ⚡ Trigger settings — its OWN modal (opened from the footer button, like
  // agent settings): the condition builder + dry-run + fire + status live
  // there; this modal keeps only the compact summary below.
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const prompt = usePrompt();

  // ─── Workflow triggers (the trigger contract) ────────────────────────────
  // This node's rows of the BUNDLE-level workflowTriggers list; the workflow
  // names come from the project's workflows index (once, on first render of
  // the section — a picker, never free text when the backend is wired).
  const [wfNames, setWfNames] = useState<string[] | null>(null);
  useEffect(() => {
    if (!projectAdapter || !projectId || wfNames !== null) return;
    let alive = true;
    projectAdapter.listWorkflows(projectId)
      .then((ws) => { if (alive) setWfNames(ws.map((w) => w.id)); })
      .catch(() => { if (alive) setWfNames([]); });
    return () => { alive = false; };
  }, [projectAdapter, projectId, wfNames]);
  const nodeTriggers = (bundle?.workflowTriggers ?? []).filter(
    (t) => t.sourceNode === node.id,
  );
  const writeTriggers = (next: import('../engine/types').WorkflowTrigger[]): void => {
    onSetWorkflowTriggers?.([
      ...(bundle?.workflowTriggers ?? []).filter((t) => t.sourceNode !== node.id),
      ...next,
    ]);
  };

  const commit = (next: GraphNode) => {
    setDraft(next);
    onUpdate?.(next);
  };

  const setKind = (kind: NodeKind) => {
    if (kind === 'gate' && !draft.gate) {
      commit({ ...draft, kind, gate: { fn: 'pass', params: [] } });
    } else if (kind !== 'gate') {
      const { gate: _g, ...rest } = draft;
      void _g;
      commit({ ...rest, kind });
    } else {
      commit({ ...draft, kind });
    }
  };
  const setGateFn = (fn: GateFn) =>
    commit({ ...draft, kind: 'gate', gate: { ...(draft.gate ?? { params: [] }), fn } });

  // ─── Param editing with uniqueness validation + rename ──────────────────
  const allParams = useMemo(
    () => [...draft.params, ...(draft.gate?.params ?? [])],
    [draft],
  );
  const keyCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of allParams) m.set(p.key, (m.get(p.key) ?? 0) + 1);
    return m;
  }, [allParams]);

  const updateParam = (target: 'node' | 'gate', idx: number, patch: Partial<ParamSpec>) => {
    const specs = target === 'node' ? draft.params : draft.gate?.params ?? [];
    const next = specs.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    if (target === 'node') commit({ ...draft, params: next });
    else if (draft.gate) commit({ ...draft, gate: { ...draft.gate, params: next } });
  };
  const renameParam = (target: 'node' | 'gate', idx: number, newKey: string) => {
    const specs = target === 'node' ? draft.params : draft.gate?.params ?? [];
    const oldKey = specs[idx]?.key;
    if (!oldKey || oldKey === newKey) return;
    updateParam(target, idx, { key: newKey });
    onSetParamKey?.(oldKey, newKey);
  };
  const addParam = (target: 'node' | 'gate') => {
    const newParam: ParamSpec = {
      key: `param${Math.floor(Math.random() * 1e6).toString(36)}`,
      label: 'New param',
      value: 0,
      min: 0,
      max: 100,
      step: 1,
    };
    if (target === 'node') commit({ ...draft, params: [...draft.params, newParam] });
    else if (draft.gate) commit({ ...draft, gate: { ...draft.gate, params: [...draft.gate.params, newParam] } });
  };
  const removeParam = (target: 'node' | 'gate', idx: number) => {
    if (target === 'node') commit({ ...draft, params: draft.params.filter((_, i) => i !== idx) });
    else if (draft.gate) commit({ ...draft, gate: { ...draft.gate, params: draft.gate.params.filter((_, i) => i !== idx) } });
  };

  // ─── ID rename with uniqueness check (#6) ───────────────────────────────
  const idTaken = existingIds.filter((id) => id !== node.id).includes(idDraft);
  const commitId = () => {
    setEditingId(false);
    if (idDraft !== node.id && !idTaken && onRenameNode) {
      if (onRenameNode(node.id, idDraft)) {
        // The rename flows through the adapter; our draft.id will refresh on
        // the next bundle publish. Don't mutate draft here — the App swaps
        // the open modal's `node` prop when the bundle changes.
      } else {
        setIdDraft(node.id); // revert
      }
    } else {
      setIdDraft(node.id);
    }
  };

  // ─── Out-of-band group membership ────────────────────────────────────────
  // A node belongs to a group when spatially inside its rect OR explicitly
  // listed in `groupIds` (see src/graph/groups.ts). The pill toggles the
  // explicit entry — clicking adds the group id, clicking again removes it.
  // Spatial membership is unchanged (still derived from the rect).
  const toggleGroup = (groupId: string) => {
    const cur = draft.groupIds ?? [];
    const next = cur.includes(groupId)
      ? cur.filter((x) => x !== groupId)
      : [...cur, groupId];
    commit({ ...draft, groupIds: next.length > 0 ? next : undefined });
  };
  const groupsInBundle: NodeGroup[] = bundle?.nodeGroups ?? [];

  // ─── Computed Metrics (State Formulas) ──────────────────────────────────
  const addComputedMetric = () => {
    const m: ComputedMetric = {
      key: `metric_${Math.floor(Math.random() * 1e6)}`,
      label: 'New Metric',
      expr: 'self.stock * 2',
    };
    commit({ ...draft, computedMetrics: [...(draft.computedMetrics ?? []), m] });
  };
  const updateComputedMetric = (idx: number, patch: Partial<ComputedMetric>) => {
    if (!draft.computedMetrics) return;
    const next = draft.computedMetrics.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    commit({ ...draft, computedMetrics: next });
  };
  const removeComputedMetric = (idx: number) => {
    if (!draft.computedMetrics) return;
    commit({ ...draft, computedMetrics: draft.computedMetrics.filter((_, i) => i !== idx) });
  };
  /** Rename a metric id (key). The engine stores computed values under this
   *  key in nodeState, so a rename leaves a stale entry until the next tick —
   *  acceptable, the value simply rolls over. Returns true if accepted. */
  const renameComputedMetric = (idx: number, newKey: string): boolean => {
    const cur = draft.computedMetrics?.[idx];
    if (!cur || cur.key === newKey) return false;
    if ((draft.computedMetrics ?? []).some((m, i) => i !== idx && m.key === newKey)) return false;
    updateComputedMetric(idx, { key: newKey });
    return true;
  };

  // ─── Formula gate: live-editable CEL expr ───────────────────────────────
  const formulaScope = scope ?? { self: { stock: values?.stock ?? 0, inflow: values?.inflow ?? 0 } };
  // Legacy formula gates (pre `gate.expr`) smuggled the CEL expr as a gate
  // param keyed 'expr' with a string value — a wire-contract violation the
  // Python side rejects. Read it for display; any edit migrates to gate.expr.
  const legacyExprVal = draft.gate?.params.find((p) => p.key === 'expr')?.value as unknown;
  const legacyExpr = typeof legacyExprVal === 'string' ? legacyExprVal : undefined;
  const formulaExpr = draft.gate?.expr ?? legacyExpr ?? '';
  const setFormulaExpr = (expr: string) => {
    if (!draft.gate) return;
    commit({
      ...draft,
      gate: { ...draft.gate, expr, params: draft.gate.params.filter((p) => p.key !== 'expr') },
    });
  };

  const iconEntry = lookupIcon(draft.icon);

  // Header (#5): icon button (click → picker) + kind <select> side by side.
  const headerLeft = (
    <>
      <button
        type="button"
        className="node-header-icon"
        title={editable ? 'Click to change icon' : 'Icon'}
        disabled={!editable}
        onClick={() => editable && setIconPickerOpen(true)}
      >
        {iconEntry ? <iconEntry.Component size={22} weight="regular" /> : <span className="actor-node-icon">{draft.icon}</span>}
      </button>
      {editable ? (
        <select
          className="seg-select node-kind-select"
          value={draft.kind}
          onChange={(e) => setKind(e.target.value as NodeKind)}
          title="Node type"
        >
          {NODE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      ) : (
        <code className="node-kind-readout">{draft.kind}</code>
      )}
    </>
  );

  // The agent + trigger buttons live in the Modal FOOTER (per spec), not the
  // body. Each opens its own settings modal; the dot signals something is
  // already configured there.
  const footer = (
    <>
      {editable && (
        <button
          type="button"
          className="btn btn-mini btn-agent"
          title="API / Agent settings"
          onClick={() => setAgentModalOpen(true)}
        >
          <Plugs size={14} weight="fill" />
          {draft.agentConfig ? 'Edit agent' : 'Add agent'}
          {draft.agentConfig && <span className="agent-dot" title="Agent configured" />}
        </button>
      )}
      {(editable || nodeTriggers.length > 0) && onSetWorkflowTriggers && (
        <button
          type="button"
          className="btn btn-mini btn-agent"
          title="⚡ Workflow trigger settings — condition builder, dry-run, fire, arm/disarm"
          onClick={() => setTriggerModalOpen(true)}
        >
          <Bolt size={14} weight="fill" />
          {nodeTriggers.length > 0 ? 'Trigger settings' : 'Add trigger'}
          {nodeTriggers.length > 0 && <span className="agent-dot" title="Trigger configured" />}
        </button>
      )}
    </>
  );

  return (
    <Modal headerLeft={headerLeft} footer={footer} onClose={onClose}>
      {/* ─── Name (no fuking label) + ID ─────────────────────────────── */}
      <section className="modal-section">
        {editable ? (
          <div className="node-name-group">
            <input
              type="text"
              className="text-input node-name-input"
              placeholder="Node name"
              value={draft.label}
              onChange={(e) => commit({ ...draft, label: e.target.value })}
            />
            {/* ID button lives in the name input's trailing addon. Click toggles
                the small ID edit input below. */}
            <button
              type="button"
              className="node-id-addon"
              title="ID"
              onClick={() => {
                if (!editable) return;
                setEditingId(true);
                setIdDraft(node.id);
              }}
              disabled={!editable}
            >
              ID
            </button>
          </div>
        ) : (
          <div className="text-input node-name-input read-only">{draft.label || '—'}</div>
        )}

        {/* ID edit: toggled by the addon button above. Leading "ID" label
            addon + trailing check button to save + close. */}
        {editable && editingId && (
          <div className="node-id-edit">
            <span className="node-id-edit-label">ID</span>
            <input
              type="text"
              className={`text-input node-id-input${idTaken ? ' invalid' : ''}`}
              value={idDraft}
              onChange={(e) => setIdDraft(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitId();
                if (e.key === 'Escape') {
                  setIdDraft(node.id);
                  setEditingId(false);
                }
              }}
            />
            <button
              type="button"
              className="node-id-save"
              title="Save ID"
              disabled={idTaken || idDraft.length === 0}
              onClick={commitId}
            >
              <Check size={14} weight="bold" />
            </button>
            {idTaken && <span className="id-hint">already in use</span>}
          </div>
        )}

        {/* Tick readout lives directly under name/ID. No header — the labels
            (Inflow/Outflow/Stock + metric names) are self-explanatory. The
            last cell is a "+" placeholder that opens the metrics modal.
            For a brand-new node there are no computed values yet, but the
            block (incl. the "+" entry point) still renders so the user can
            add metrics immediately — readouts just show 0 until first tick. */}
        {(values || editable) && (
          <div className="modal-kv-grid node-tick-grid">
            <KV label="Inflow" value={fmt(values?.inflow ?? 0)} />
            <KV label="Outflow" value={fmt(values?.outflow ?? 0)} />
            <KV label="Stock" value={fmt(values?.stock ?? 0)} />
            {/* Iterate over the metric definitions (source of truth), not the
                engine's computed state — so a freshly added metric appears in
                the grid immediately, before the next tick computes its value. */}
            {(draft.computedMetrics ?? []).map((m) => {
              const v = values?.state?.[m.key];
              return <KV key={m.key} label={m.label || m.key} value={v == null ? '—' : fmt(v)} />;
            })}
            {editable && (
              <button
                type="button"
                className="kv-add"
                onClick={() => setMetricModalOpen(true)}
                title="Add metric"
              >
                <Plus size={14} weight="bold" />
              </button>
            )}
          </div>
        )}
        {/* Provenance — WHERE these numbers come from. In api/relay mode the
            tick is computed by the project's model subprocess and posted to
            the relay; graph edits (weights, params) steer only the in-browser
            engine, so the honest hint is "the model is the source". */}
        {values && (
          <div className="modal-hint">
            {flowsFromModel
              ? 'Values from the latest tick posted by the running model — graph weights steer only the in-browser engine.'
              : 'Values computed by the in-browser engine from this graph.'}
          </div>
        )}
      </section>

      {/* ─── Outflow routing — where this node's outflow goes and how the
          split is decided. Two DIFFERENT numbers per edge, both shown:
          the LIVE share (this tick's flow ÷ this node's outflow — what is
          actually happening) and the AUTHORED weight (a static number in
          the bundle that the in-browser engine normalises across siblings;
          in api/relay mode the model's flows are the truth and the weight
          is only the local-engine default). When they diverge the authored
          number turns amber — that's "the knob moved, the graph hasn't". */}
      {(() => {
        const outEdges = (bundle?.graph.edges ?? []).filter((e) => e.source === node.id);
        if (outEdges.length === 0) return null;
        const nodeById = new Map((bundle?.graph.nodes ?? []).map((n) => [n.id, n]));
        const routing = outEdges.filter((e) => e.flowKind !== 'signal');
        const signals = outEdges.filter((e) => e.flowKind === 'signal');
        const sumW = routing.reduce((acc, e) => acc + (e.weight || 0), 0);
        // The latest captured tick — the LIVE half of each row.
        const last = recentTicks && recentTicks.length > 0 ? recentTicks[recentTicks.length - 1] : undefined;
        const liveOutflow = last?.nodeValues?.[node.id]?.outflow;
        // One line: what this node DOES with what arrives (its outflow rule).
        const rule =
          draft.kind === 'source'
            ? `emits its rate param '${draft.source?.rateParam ?? 'rate'}'${draft.source?.distribution && draft.source.distribution !== 'constant' ? ` (${draft.source.distribution} noise)` : ''} each tick`
            : draft.kind === 'gate'
              ? `gate '${draft.gate?.fn ?? 'pass'}'${draft.gate?.fn === 'formula' && draft.gate.expr ? ` — outflow = ${draft.gate.expr}` : ''}`
              : draft.kind === 'sink'
                ? 'absorbs inflow (terminal — no outflow)'
                : draft.kind === 'delay'
                  ? 'releases inflow after the transit delay'
                  : draft.kind === 'assembler'
                    ? 'produces from its recipe inputs'
                    : 'passes stock + inflow through as outflow';
        return (
          <section className="modal-section">
            <div className="modal-subhead">Outflow routing</div>
            <div className="modal-kv"><span>Outflow rule</span><code>{rule}</code></div>
            {(() => {
              const rows = routing.map((e) => {
                const share = sumW > 0 ? (e.weight || 0) / sumW : 0;
                const liveFlow = last?.edgeFlows?.[e.id];
                const liveShare = liveOutflow && liveOutflow > 0 && liveFlow !== undefined ? liveFlow / liveOutflow : undefined;
                const stale = liveShare !== undefined && Math.abs(liveShare - share) > 0.005;
                const liveTitle = liveShare !== undefined
                  ? `live: flow ${fmt(liveFlow ?? 0)} = ${Math.round(liveShare * 100)}% of this node's outflow ${fmt(liveOutflow ?? 0)}`
                  : '';
                const authoredTitle = `authored weight ${e.weight} → ${Math.round(share * 100)}% after normalisation${stale ? ' (STALE — differs from live)' : ''}`;
                return (
                  <div className="modal-kv" key={e.id}>
                    <span title={`Edge ${e.id} — double-click it on the canvas to edit its weight`}>
                      → {nodeById.get(e.target)?.label ?? e.target}
                    </span>
                    <code title={`${liveTitle}${liveTitle && authoredTitle ? ' · ' : ''}${authoredTitle}`}>
                      {liveShare !== undefined && (
                        <>live {fmt(liveFlow ?? 0)} · {Math.round(liveShare * 100)}%{' '}</>
                      )}
                      <span className={stale ? 'routing-authored routing-stale' : 'routing-authored'}>
                        w {e.weight}
                      </span>
                      {(e.connections ?? []).length > 0 ? ' · ƒ typed link' : ''}
                    </code>
                  </div>
                );
              });
              const anyStale = routing.some((e) => {
                const lf = last?.edgeFlows?.[e.id];
                const ls = liveOutflow && liveOutflow > 0 && lf !== undefined ? lf / liveOutflow : undefined;
                const sh = sumW > 0 ? (e.weight || 0) / sumW : 0;
                return ls !== undefined && Math.abs(ls - sh) > 0.005;
              });
              return (
                <>
                  {rows}
                  {anyStale && editable && onUpdateEdge && (
                    <button
                      type="button"
                      className="btn btn-mini btn-add"
                      title="Write this node's LIVE split (from the latest tick) back as the authored weights — clears the amber staleness"
                      onClick={() => {
                        for (const e of routing) {
                          const lf = last?.edgeFlows?.[e.id];
                          if (lf === undefined || !liveOutflow || liveOutflow <= 0) continue;
                          const nextW = Math.round((lf / liveOutflow) * 1000) / 1000;
                          onUpdateEdge({ ...e, weight: nextW === 0 ? 0.001 : nextW });
                        }
                      }}
                    >
                      <Check size={14} weight="bold" /> adopt live split as weights
                    </button>
                  )}
                </>
              );
            })()}
            {routing.length > 1 && (
              <div className="modal-hint">
                Σ weights {sumW.toFixed(2)} —{' '}
                {Math.abs(sumW - 1) < 1e-9
                  ? 'shares are exact (Σw = 1).'
                  : 'weights are NORMALISED: each edge\u2019s share = its w ÷ Σw.'}
              </div>
            )}
            {signals.map((e) => (
              <div className="modal-kv" key={e.id}>
                <span>⇢ {nodeById.get(e.target)?.label ?? e.target}</span>
                <code>signal — carries a value, no flow</code>
              </div>
            ))}
            {flowsFromModel && (
              <div className="modal-hint">
                The <strong>live</strong> numbers are this tick&apos;s flows posted by the running model (its knobs — the
                params table cells — decide them). The <strong>w</strong> numbers are static, authored in the graph: they
                steer only the in-browser engine, and go stale (amber) when the model&apos;s knobs move away from them.
              </div>
            )}
          </section>
        );
      })()}

      {/* ─── Node parameters (#7) ────────────────────────────────────── */}
      <section className="modal-section">
        <div className="modal-subhead">Parameters</div>
        {draft.params.map((p, i) => (
          <ParamRow
            key={p.key + i}
            spec={p}
            editable={editable}
            duplicate={keyCounts.get(p.key)! > 1}
            onChange={(patch) => updateParam('node', i, patch)}
            onRename={(nk) => renameParam('node', i, nk)}
            onRemove={() => removeParam('node', i)}
            onLiveChange={(v) => onSetParam(p.key, v)}
            bindingTools={bindingTools}
          />
        ))}
        {editable && (
          <button type="button" className="btn btn-mini btn-add" onClick={() => addParam('node')}>
            <Plus size={14} weight="bold" /> node param
          </button>
        )}
        {/* Conservation opt-out — the accounting-boundary escape hatch for
            nodes whose inflow/outflow mix units. Assembler kinds default off
            (the engine's kind-aware default); an explicit flip always wins. */}
        {editable && (
          <div className="modal-kv">
            <span>Conserves stock</span>
            <input
              type="checkbox"
              checked={draft.conserves ?? draft.kind !== 'assembler'}
              onChange={(e) => commit({ ...draft, conserves: e.target.checked })}
              title="Assert stock = prev + inflow − outflow at this node each tick"
            />
          </div>
        )}
        {/* Unit — the stock's dimension, purely declarative. Feeds the
            unit-consistency lint: mixed units at a conserving interior node
            are flagged (mark conserves=false or split the boundary). */}
        {editable && (
          <div className="modal-kv">
            <span>Unit</span>
            <input
              type="text"
              className="text-input"
              style={{ width: 120 }}
              placeholder="e.g. meals, $, kWh"
              value={draft.unit ?? ''}
              onChange={(e) => commit({ ...draft, unit: e.target.value || undefined })}
              title="The stock's unit/dimension — documentation + the unit-consistency lint"
            />
          </div>
        )}
        {/* Headline quantity — which per-tick number the canvas renders under
            the node's label (GraphNode.displayValue). Stock is the default;
            pass-through nodes often want inflow/outflow instead, 'Hidden'
            clears the number entirely. Computed metrics join the list as
            their state keys. */}
        {editable && (
          <div className="modal-kv">
            <span>Headline value</span>
            <select
              className="seg-select node-kind-select"
              value={draft.displayValue ?? 'stock'}
              onChange={(e) => commit({ ...draft, displayValue: e.target.value === 'stock' ? undefined : e.target.value as NodeDisplayValue })}
              title="Which per-tick quantity the canvas shows as this node's headline number"
            >
              <option value="stock">Stock (default)</option>
              <option value="inflow">Inflow</option>
              <option value="outflow">Outflow</option>
              {(draft.computedMetrics ?? []).map((m) => (
                <option key={m.key} value={`state:${m.key}`}>{m.label || m.key} (metric)</option>
              ))}
              <option value="none">Hidden</option>
            </select>
          </div>
        )}
      </section>

      {/* ─── Gate (when kind === gate) ────────────────────────────────── */}
      {draft.kind === 'gate' && (
        <section className="modal-section">
          <div className="modal-subhead">Gate</div>
          {editable ? (
            <select className="seg-select" value={draft.gate?.fn ?? 'pass'} onChange={(e) => setGateFn(e.target.value as GateFn)}>
              {GATE_FNS.map((fn) => <option key={fn} value={fn}>{fn}</option>)}
            </select>
          ) : (
            <div className="modal-kv"><span>Function</span><code>{draft.gate?.fn}</code></div>
          )}
          {editable ? (
            <div className="modal-kv">
              <span>Capacity Param</span>
              <select
                className="seg-select"
                value={draft.gate?.capacityParam ?? 'capacity'}
                onChange={(e) => draft.gate && commit({ ...draft, gate: { ...draft.gate, capacityParam: e.target.value } })}
              >
                {allParams.length === 0 && <option value="capacity" disabled>No params defined (uncapped)</option>}
                {!allParams.some((p) => p.key === (draft.gate?.capacityParam ?? 'capacity')) && (
                  <option value={draft.gate?.capacityParam ?? 'capacity'}>{draft.gate?.capacityParam ?? 'capacity'} (missing)</option>
                )}
                {allParams.map((p) => (
                  <option key={p.key} value={p.key}>{p.key}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="modal-kv"><span>Capacity Param</span><code>{draft.gate?.capacityParam ?? 'capacity'}</code></div>
          )}
          {draft.gate?.fn === 'formula' && (
            <ExprField
              label="Outflow formula"
              value={formulaExpr || ''}
              scope={formulaScope}
              onChange={setFormulaExpr}
              placeholder=" e.g. self.stock > 5 ? self.inflow : 0"
            />
          )}
          {draft.gate && draft.gate.params.filter((p) => p.key !== 'expr').length > 0 && (
            <div className="modal-subhead">Gate parameters</div>
          )}
          {draft.gate?.params
            .map((p, i) => ({ p, i }))
            .filter(({ p }) => p.key !== 'expr')
            .map(({ p, i }) => (
              <ParamRow
                key={p.key}
                spec={p}
                editable={editable}
                duplicate={keyCounts.get(p.key)! > 1}
                onChange={(patch) => updateParam('gate', i, patch)}
                onRename={(nk) => renameParam('gate', i, nk)}
                onRemove={() => removeParam('gate', i)}
                onLiveChange={(v) => onSetParam(p.key, v)}
                bindingTools={bindingTools}
              />
            ))}
          {editable && draft.gate && (
            <button type="button" className="btn btn-mini btn-add" onClick={() => addParam('gate')}>
              <Plus size={14} weight="bold" /> gate param
            </button>
          )}
        </section>
      )}

      {/* ─── Source (when kind === source) ────────────────────────────────── */}
      {draft.kind === 'source' && (
        <section className="modal-section">
          <div className="modal-subhead">Source</div>
          {editable ? (
            <>
              <div className="modal-kv">
                <span>Rate Param</span>
                <select 
                  className="seg-select" 
                  value={draft.source?.rateParam ?? 'rate'} 
                  onChange={(e) => commit({ ...draft, source: { ...draft.source, rateParam: e.target.value } })}
                >
                  {draft.params.length === 0 && <option value="rate" disabled>No params defined</option>}
                  {!draft.params.some(p => p.key === (draft.source?.rateParam ?? 'rate')) && <option value={draft.source?.rateParam ?? 'rate'}>{draft.source?.rateParam ?? 'rate'} (missing)</option>}
                  {draft.params.map((p) => (
                    <option key={p.key} value={p.key}>{p.key}</option>
                  ))}
                </select>
              </div>
              <div className="modal-kv">
                <span>Distribution</span>
                <select className="seg-select" value={draft.source?.distribution ?? 'constant'} onChange={(e) => commit({ ...draft, source: { ...draft.source, distribution: e.target.value as any } })}>
                  <option value="constant">constant</option>
                  <option value="uniform">uniform</option>
                  <option value="normal">normal</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="modal-kv"><span>Rate Param</span><code>{draft.source?.rateParam ?? 'rate'}</code></div>
              <div className="modal-kv"><span>Distribution</span><code>{draft.source?.distribution ?? 'constant'}</code></div>
            </>
          )}
        </section>
      )}

      {/* ─── Delay (when kind === delay) ────────────────────────────────── */}
      {draft.kind === 'delay' && (
        <section className="modal-section">
          <div className="modal-subhead">Delay</div>
          {editable ? (
            <div className="modal-kv">
              <span>Ticks Param</span>
              <select 
                className="seg-select" 
                value={draft.delay?.ticksParam ?? 'ticks'} 
                onChange={(e) => commit({ ...draft, delay: { ...draft.delay, ticksParam: e.target.value } })}
              >
                {draft.params.length === 0 && <option value="ticks" disabled>No params defined</option>}
                {!draft.params.some(p => p.key === (draft.delay?.ticksParam ?? 'ticks')) && <option value={draft.delay?.ticksParam ?? 'ticks'}>{draft.delay?.ticksParam ?? 'ticks'} (missing)</option>}
                {draft.params.map((p) => (
                  <option key={p.key} value={p.key}>{p.key}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="modal-kv"><span>Ticks Param</span><code>{draft.delay?.ticksParam ?? 'ticks'}</code></div>
          )}
        </section>
      )}

      {/* ─── Assembler (when kind === assembler) ────────────────────────────────── */}
      {draft.kind === 'assembler' && (
        <section className="modal-section">
          <div className="modal-subhead">Assembler Recipe</div>
          {Object.entries(draft.assembler?.recipe ?? {}).map(([key, amount]) => (
            <div key={key} className="modal-kv">
              <span>{key}</span>
              {editable ? (
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input 
                    type="number" 
                    step="any"
                    className="seg-input" 
                    style={{ width: '80px' }} 
                    value={amount} 
                    onChange={(e) => {
                      const recipe = { ...(draft.assembler?.recipe ?? {}) };
                      recipe[key] = parseFloat(e.target.value) || 0;
                      commit({ ...draft, assembler: { ...draft.assembler, recipe } });
                    }} 
                  />
                  <button 
                    type="button" 
                    className="btn btn-mini" 
                    onClick={() => {
                      const recipe = { ...(draft.assembler?.recipe ?? {}) };
                      delete recipe[key];
                      commit({ ...draft, assembler: { ...draft.assembler, recipe } });
                    }}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ) : (
                <code>{amount}</code>
              )}
            </div>
          ))}
          {editable && (
            <button
              type="button"
              className="btn btn-mini btn-add"
              onClick={async () => {
                const key = await prompt({
                  title: 'Add ingredient',
                  body: 'Ingredient key — a flow kind or custom kind (e.g. goods, parts).',
                  confirmLabel: 'Add',
                });
                if (key) {
                  const recipe = { ...(draft.assembler?.recipe ?? {}) };
                  recipe[key] = 1;
                  commit({ ...draft, assembler: { ...draft.assembler, recipe } });
                }
              }}
            >
              <Plus size={14} weight="bold" /> ingredient
            </button>
          )}
        </section>
      )}

      {/* ─── Out-of-band group membership ─────────────────────────────────
          Only rendered when the bundle has at least one spatial group. Each
          group is a pill; selecting it adopts the group's colour (visual
          signal that the node is now a member). Spatial membership is
          unaffected — this only toggles the explicit `groupIds` entry. */}
      {groupsInBundle.length > 0 && (
        <section className="modal-section">
          <div className="modal-subhead">Groups</div>
          <div className="group-pills">
            {groupsInBundle.map((g) => {
              const color = groupColorOf(g);
              const on = (draft.groupIds ?? []).includes(g.id);
              return (
                <button
                  key={g.id}
                  type="button"
                  className={`group-pill${on ? ' on' : ''}`}
                  style={on ? { background: color, borderColor: color } : undefined}
                  title={on ? `Remove from ${g.label}` : `Add to ${g.label}`}
                  onClick={() => editable && toggleGroup(g.id)}
                  disabled={!editable}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── Workflow triggers (the trigger contract) ──────────────────────
          This node's declarative signal→workflow links. The rows edit the
          BUNDLE-level workflowTriggers list filtered to this node. The FULL
          editor (condition builder + dry-run + fire + arm/disarm + status)
          is the TriggerSettingsModal — opened from the footer's "Trigger
          settings" button, like agent settings; this section is the compact
          summary. The backend's relay path evaluates on:'expr' entries
          every tick. */}
      {(editable ? onSetWorkflowTriggers : nodeTriggers.length > 0) ? (
        <section className="modal-section">
          <div className="modal-subhead">Workflow triggers</div>
          {nodeTriggers.length === 0 && (
            <p className="param-hint" style={{ marginTop: 0 }}>
              Start a durable workflow when a condition over the tick holds —
              e.g. an anomaly level crossing a threshold. Use the{" "}
              <strong>Trigger settings</strong> button (bottom) to add one.
            </p>
          )}
          {nodeTriggers.map((t) => {
            const st = triggerStatus?.[t.id];
            return (
              <div key={t.id} className="trigger-row" style={{ marginBottom: 6 }}>
                <Bolt size={13} style={{ opacity: t.enabled === false ? 0.35 : 1 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="trigger-row-title">{t.label || t.id} → {t.workflow}</div>
                  <div className="trigger-row-meta">
                    {t.on === 'expr'
                      ? <code>{t.expr}</code>
                      : t.on === 'cron'
                        ? <><span>cron</span> <code>{t.schedule}</code></>
                        : <span>manual</span>}
                    {t.enabled === false && <span>disarmed</span>}
                    {st?.lastError
                      ? <span className="trigger-status-error" title={st.lastError}>last fire FAILED</span>
                      : st?.lastFiredStep != null
                        ? <span>fired @ step {st.lastFiredStep}</span>
                        : <span>never fired</span>}
                  </div>
                </div>
                <button
                  type="button" className="code-task-btn" title="Open trigger settings"
                  onClick={() => setTriggerModalOpen(true)}
                >
                  Edit
                </button>
              </div>
            );
          })}
        </section>
      ) : null}

      {/* ─── API / Agent Settings ─────────────────────────────────────────
          The full form lives in a separate AgentModal. The button that opens
          it is in the Modal footer (see `footer` above). In view mode a
          read-only summary is shown when an agent is configured. */}
      {!editable && draft.agentConfig && (
        <section className="modal-section">
          <div className="modal-subhead">Agent</div>
          <div className="modal-kv-grid">
            {draft.agentConfig.name && <KV label="Name" value={draft.agentConfig.name} />}
            <KV label="Mode" value={draft.agentConfig.mode ?? 'api'} />
            <KV label="Target" value={draft.agentConfig.target || '—'} />
            {draft.agentConfig.linkedDocumentId && <KV label="Linked doc" value={draft.agentConfig.linkedDocumentId} />}
          </div>
        </section>
      )}

      {iconPickerOpen && (
        <IconPickerModal
          current={draft.icon}
          onPick={(name) => commit({ ...draft, icon: name })}
          onClose={() => setIconPickerOpen(false)}
        />
      )}

      {agentModalOpen && (
        <AgentModal
          host={{
            title: draft.label || node.id,
            paramOptions: allParams.map((p) => ({ key: p.key, label: p.label || p.key, path: `${node.id}.${p.key}` })),
            inputDescription:
              'All inflow edge data for this node is sent as input on every tick.',
          }}
          config={draft.agentConfig}
          editable={editable}
          libraryAdapter={libraryAdapter}
          projectAdapter={projectAdapter}
          projectId={projectId}
          onClose={() => setAgentModalOpen(false)}
          onChange={(cfg) => commit({ ...draft, agentConfig: cfg })}
        />
      )}

      {metricModalOpen && (
        <MetricsEditModal
          title="Node Metrics"
          metrics={draft.computedMetrics ?? []}
          scope={formulaScope}
          onClose={() => setMetricModalOpen(false)}
          onAdd={addComputedMetric}
          onUpdate={updateComputedMetric}
          onRemove={removeComputedMetric}
          onRenameKey={renameComputedMetric}
          keyTaken={(key, idx) => (draft.computedMetrics ?? []).some((m, i) => i !== idx && m.key === key)}
        />
      )}

      {/* ⚡ Trigger settings — the node-scoped trigger editor (condition
          builder + dry-run + fire + arm/disarm + status), opened from the
          footer button like agent settings. Writes merge this node's rows
          back into the bundle's full list (sourceNode pinned to this node). */}
      {triggerModalOpen && bundle && onSetWorkflowTriggers && (
        <TriggerSettingsModal
          title={`Trigger settings — ${node.label || node.id}`}
          triggers={nodeTriggers}
          bundle={bundle}
          editable={editable}
          projectAdapter={projectAdapter}
          projectId={projectId}
          recentTicks={recentTicks ?? []}
          triggerStatus={triggerStatus ?? {}}
          fireTrigger={onFireWorkflow}
          onClose={() => setTriggerModalOpen(false)}
          onChange={(next) => writeTriggers(
            next.map((t) => ({ ...t, sourceNode: t.sourceNode ?? node.id })),
          )}
        />
      )}
    </Modal>
  );
}

/**
 * ParamRow — one row = [label input + key input] (inline, 50% each) + slider +
 * value readout + range hint + link + trash-icon buttons. The label is the
 * human-readable name shown in pills/menus; the key is the machine id used in
 * formulas. #7.
 *
 * Cell binding (spec.binding): when bound, the DB cell is the source of
 * truth — the slider shows/patches the CELL (write-through via the tables
 * PATCH seam + setParam for immediate engine effect), the saved `value`
 * becomes a fallback label, and a broken binding (missing row/cell) keeps the
 * fallback in effect with a ⚠ badge. The link button opens the inline picker.
 */
export function ParamRow({
  spec,
  editable,
  duplicate,
  onChange,
  onRename,
  onRemove,
  onLiveChange,
  bindingTools,
  hideLabel = false,
}: {
  spec: ParamSpec;
  editable: boolean;
  duplicate: boolean;
  onChange: (patch: Partial<ParamSpec>) => void;
  onRename: (newKey: string) => void;
  onRemove: () => void;
  onLiveChange: (value: number) => void;
  bindingTools?: BindingTools;
  /** Edge variant (EdgeModal reuses this row): key input only — edge params
   *  historically have no separate label field. */
  hideLabel?: boolean;
}): JSX.Element {
  const step = spec.step ?? (spec.max - spec.min) / 100;
  const [pickerOpen, setPickerOpen] = useState(false);
  const bound = spec.binding ?? null;
  const lookup = bound && bindingTools ? lookupBoundCell(bindingTools.tables, bound) : null;
  const liveValue = lookup?.value ?? null; // null = broken (fallback in effect)
  const sliderValue = bound ? (liveValue ?? spec.value) : spec.value;
  if (!editable) {
    return (
      <div className="param-row">
        <label className="param-slider" title={spec.hint}>
          <div className="param-slider-head">
            <span>{spec.label}</span>
            <code>{spec.key} = {bound ? (liveValue ?? '⚠ ' + spec.value) : spec.value}</code>
          </div>
          <div className="param-hint">{spec.hint}</div>
        </label>
      </div>
    );
  }
  return (
    <div className="param-row">
      <div className="param-row-line">
        {/* Label + key replace the old single key input, in place on the same
            line. Two inputs at 50% each, 0 gap, then the value readout + trash
            button to the right (unchanged from before). */}
        <div className="param-row-namekey">
          {hideLabel ? (
            <input
              type="text"
              className={`text-input param-key${duplicate ? ' invalid' : ''}`}
              value={spec.key}
              onChange={(e) => onRename(e.target.value)}
              placeholder="param key"
              title="machine key (used in connection transforms as params.<key>)"
            />
          ) : (
            <>
              <input
                type="text"
                className="text-input param-label"
                value={spec.label}
                onChange={(e) => onChange({ label: e.target.value })}
                placeholder="name"
                title="human-readable name (shown in pills, menus)"
              />
              <input
                type="text"
                className={`text-input param-key${duplicate ? ' invalid' : ''}`}
                value={spec.key}
                onChange={(e) => onRename(e.target.value)}
                placeholder="id"
                title={duplicate ? 'duplicate key — must be unique within the node' : 'machine key (used in formulas + references)'}
              />
            </>
          )}
        </div>
        <span className="param-value-readout" title={bound && liveValue == null ? 'binding broken — showing the saved fallback' : undefined}>
          {bound ? (liveValue ?? `⚠ ${fmt(spec.value)}`) : fmt(spec.value)}
        </span>
        {/* Link-icon button — bind the param to a table cell (DB-driven). */}
        <button
          type="button"
          className={`input-ext-btn${bound ? ' on' : ''}`}
          title={bound ? 'Edit table-cell binding' : 'Bind to a table cell (the DB drives this value)'}
          onClick={() => setPickerOpen((o) => !o)}
        >
          {bound ? <Link size={14} /> : <LinkBreak size={14} className="opacity-icon" />}
        </button>
        {/* Trash-icon as input-extension button — removes the param. */}
        <button type="button" className="input-ext-btn" title="Remove param" onClick={onRemove}>
          <Trash size={14} />
        </button>
      </div>
      {bound && (
        <BindingBadge
          binding={bound}
          tools={bindingTools}
          onOpen={() => setPickerOpen(true)}
          onUnbind={() => onChange({ binding: null })}
        />
      )}
      <div className="param-row-slider">
        <input
          type="range"
          min={spec.min}
          max={spec.max}
          step={step}
          value={sliderValue}
          title={bound ? 'Bound — writes the table cell (the DB drives this value)' : undefined}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (bound) {
              // Write-through: engine sees it next tick, the cell is PATCHed
              // (the resolver reads it back — one source of truth). The saved
              // fallback value is NOT touched.
              onLiveChange(v);
              if (lookup?.rowId != null) {
                bindingTools?.onUpdateTableCell?.(bound.table, lookup.rowId, bound.column, v);
              }
            } else {
              onChange({ value: v });
              onLiveChange(v);
            }
          }}
        />
        <span className="param-range">
          [{spec.min}, {spec.max}]{bound && <em className="param-fallback"> · fallback {fmt(spec.value)}</em>}
        </span>
      </div>
      {pickerOpen && (
        <CellBindingPicker
          value={bound}
          onChange={(b) => onChange(b ? { binding: b } : { binding: null })}
          tools={bindingTools}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return <div className="modal-kv"><span>{label}</span><code>{value}</code></div>;
}
function fmt(n: number): string {
  if (Math.abs(n) >= 10000) return (n / 1000).toFixed(1) + 'k';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}
